import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ChevronRight, 
  Clock, 
  Calendar, 
  ChevronDown, 
  ChevronUp,
  CheckCircle2,
  AlertTriangle,
  Lightbulb,
  ExternalLink,
  ArrowRight,
  Wrench,
  Truck,
  Shield,
  Euro,
  Target,
  Users,
  Building2,
  HelpCircle
} from 'lucide-react';

const HowToChooseDrilling = () => {
  const [activeSection, setActiveSection] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  // Table of contents
  const sections = [
    { id: 'section-1', title: "Qu'est-ce qu'une foreuse géotechnique ?" },
    { id: 'section-2', title: "Les 4 grandes familles de foreuses" },
    { id: 'section-3', title: "10 critères pour choisir" },
    { id: 'section-4', title: "Acheter ou louer ?" },
    { id: 'section-5', title: "Neuf ou occasion ?" },
    { id: 'section-6', title: "Les marques de référence" },
    { id: 'section-7', title: "Combien ça coûte vraiment ?" },
    { id: 'section-8', title: "Les pièges à éviter" },
    { id: 'section-9', title: "Conclusion et plan d'action" },
  ];

  const faqs = [
    {
      question: "Combien de temps dure une foreuse géotechnique ?",
      answer: "Une foreuse bien entretenue dure 15 à 25 ans. Le moteur peut être remplacé. La durée de vie utile (avant gros travaux) est généralement de 8-12 ans."
    },
    {
      question: "Quel est le délai de livraison pour une foreuse neuve ?",
      answer: "Comptez 6 à 12 semaines pour une foreuse neuve standard. Les modèles avec options spécifiques peuvent demander 3 à 6 mois."
    },
    {
      question: "Faut-il une licence spéciale pour conduire une foreuse ?",
      answer: "En France, le CACES R482 catégorie F (engins de forage) est requis pour les conducteurs de foreuses sur chantier."
    },
    {
      question: "Quelle est la différence entre une foreuse et une sondeuse ?",
      answer: "Les termes sont souvent utilisés indifféremment. Techniquement, une sondeuse est plutôt une petite/moyenne foreuse dédiée aux sondages et reconnaissances, tandis qu'une foreuse peut désigner toute la gamme (sondage + fondations spéciales)."
    },
    {
      question: "Peut-on importer une foreuse depuis l'étranger ?",
      answer: "Oui, mais attention : vérifiez la conformité CE européenne + EN 16228, anticipez les frais de douane (TVA, droits), vérifiez les normes antipollution (Stage V en Europe), et prévoyez un certificat de conformité valide."
    },
    {
      question: "Faut-il privilégier une marque européenne ?",
      answer: "Pas obligatoirement, mais en Europe les marques italiennes et allemandes dominent pour la conformité normative, le SAV et la disponibilité pièces. Une marque chinoise peut être attractive en prix mais souvent problématique sur le SAV."
    }
  ];

  // Track scroll for active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  // Schema.org JSON-LD
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Comment choisir une foreuse géotechnique ? Guide complet 2026",
    "description": "Guide expert pour choisir votre foreuse géotechnique : 10 critères, marques, neuf/occasion, prix. Conseils de professionnels du métier.",
    "image": "https://geots.consulting/og-images/guide-foreuse.jpg",
    "author": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    },
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting",
      "logo": {
        "@type": "ImageObject",
        "url": "https://geots.consulting/logo.png"
      }
    },
    "datePublished": "2026-05-09",
    "dateModified": "2026-07-23"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Comment choisir une foreuse géotechnique ? Guide complet 2026 | GEOTS</title>
        <meta name="description" content="Guide expert pour choisir votre foreuse géotechnique : 10 critères, marques, neuf/occasion, prix. Conseils de professionnels du métier." />
        <link rel="canonical" href="https://geots.consulting/guides/comment-choisir-foreuse-geotechnique" />
        <meta property="og:title" content="Comment choisir une foreuse géotechnique ? Guide complet 2026" />
        <meta property="og:description" content="Guide expert pour choisir votre foreuse géotechnique : 10 critères, marques, neuf/occasion, prix." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://geots.consulting/guides/comment-choisir-foreuse-geotechnique" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-[#635BFF]">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-gray-400">Guides</span>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0A2540] font-medium">Choisir une foreuse</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <header className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center gap-4 mb-6">
                <span className="bg-[#635BFF] text-white text-xs font-bold px-3 py-1 rounded-full">
                  GUIDE COMPLET
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Mis à jour le 23 juillet 2026
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  12 min de lecture
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Comment choisir une foreuse géotechnique ?
                <span className="text-[#00D4FF]"> Guide complet 2026</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Vous cherchez à acheter ou louer une foreuse géotechnique ? Ce guide vous accompagne 
                pas à pas dans le choix de la machine idéale selon votre activité, votre budget et 
                vos contraintes. Écrit par des professionnels du métier, pour des professionnels.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/shop" 
                  className="bg-[#635BFF] hover:bg-[#5248e5] text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  Voir les foreuses disponibles
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/comparateur" 
                  className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  Comparer les modèles
                </Link>
              </div>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="sticky top-24 bg-white rounded-xl shadow-sm border p-6">
                <h3 className="font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#635BFF]" />
                  Sommaire
                </h3>
                <nav className="space-y-2">
                  {sections.map((section, index) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-start gap-2 ${
                        activeSection === section.id
                          ? 'bg-[#635BFF]/10 text-[#635BFF] font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className="text-xs font-bold text-gray-400 mt-0.5">{index + 1}.</span>
                      {section.title}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 max-w-3xl">
              <article className="prose prose-lg max-w-none">
                
                {/* Section 1 */}
                <section id="section-1" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">1</span>
                    Qu'est-ce qu'une foreuse géotechnique ?
                  </h2>
                  
                  <p className="text-gray-700 mb-6">
                    Une <strong>foreuse géotechnique</strong> est un engin spécialisé conçu pour réaliser des sondages, 
                    des reconnaissances de sol et des opérations de forage dans le cadre d'études géotechniques 
                    ou de travaux de fondations spéciales.
                  </p>

                  <p className="text-gray-700 mb-6">
                    Contrairement à une <strong>foreuse classique</strong> (utilisée pour l'eau ou le pétrole), 
                    une foreuse géotechnique est conçue pour :
                  </p>

                  <div className="grid md:grid-cols-2 gap-4 mb-8">
                    {[
                      { icon: Target, title: "Précision", desc: "Sondages exploitables pour analyses scientifiques" },
                      { icon: Wrench, title: "Polyvalence", desc: "S'adapte à différents types de sols" },
                      { icon: Building2, title: "Compacité", desc: "Accès aux sites contraints (urbain, intérieur)" },
                      { icon: Shield, title: "Modularité", desc: "Accepte différents accessoires" },
                    ].map((item, i) => (
                      <div key={i} className="bg-white p-4 rounded-lg border flex items-start gap-3">
                        <div className="bg-[#635BFF]/10 p-2 rounded-lg">
                          <item.icon className="w-5 h-5 text-[#635BFF]" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-[#0A2540]">{item.title}</h4>
                          <p className="text-sm text-gray-600">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* CTA Box - Demande de devis */}
                  <div className="bg-gradient-to-r from-[#635BFF] to-[#7C3AED] rounded-2xl p-6 mb-8 text-white">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                      <div>
                        <h3 className="text-xl font-bold mb-1">Vous cherchez un devis pour une foreuse ?</h3>
                        <p className="text-white/80">Recevez des offres de vendeurs et loueurs qualifiés, adaptées à votre besoin.</p>
                      </div>
                      <Link 
                        to="/materiel/demande?type=foreuse" 
                        className="bg-white text-[#635BFF] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2 whitespace-nowrap"
                      >
                        Demander un devis
                        <ArrowRight className="w-5 h-5" />
                      </Link>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-[#0A2540] mb-4">Les applications principales</h3>
                  
                  <div className="bg-white rounded-xl border overflow-hidden mb-8">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#0A2540]">Application</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#0A2540]">Description</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {[
                          ["🔬 Sondages géotechniques", "Reconnaissance des sols pour études de fondation"],
                          ["📊 Essais in-situ (SPT, CPT)", "Mesure de la résistance des sols"],
                          ["💧 Installation piézomètres", "Surveillance du niveau d'eau souterraine"],
                          ["📐 Pose d'inclinomètres", "Surveillance des mouvements de terrain"],
                          ["🏗️ Micropieux", "Renforcement de fondations existantes"],
                          ["⚓ Ancrages", "Stabilisation de talus, soutènement"],
                          ["💉 Jet grouting", "Consolidation et étanchéité des sols"],
                          ["🌍 Géothermie", "Forage pour pompes à chaleur"],
                        ].map(([app, desc], i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-[#0A2540]">{app}</td>
                            <td className="px-4 py-3 text-gray-600">{desc}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Section 2 */}
                <section id="section-2" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">2</span>
                    Les 4 grandes familles de foreuses
                  </h2>

                  <p className="text-gray-700 mb-8">
                    Avant de choisir une machine, il faut comprendre <strong>les 4 grandes familles</strong> disponibles sur le marché.
                  </p>

                  {/* Family cards */}
                  {[
                    {
                      color: "green",
                      name: "Foreuses compactes (1-5 tonnes)",
                      target: "Petites entreprises, bureaux d'études, premiers achats",
                      specs: ["Poids : 1 000 - 5 000 kg", "Puissance : 30-80 CV", "Sondages superficiels, échantillonnage, SPT"],
                      examples: "Comacchio GEO 205, GEO 300, MC 3D",
                      budget: "50 000 - 150 000 € (neuf)"
                    },
                    {
                      color: "yellow",
                      name: "Foreuses moyennes (5-10 tonnes)",
                      target: "Entreprises en croissance, polyvalence requise",
                      specs: ["Poids : 5 000 - 10 000 kg", "Puissance : 80-130 CV", "Sondages profonds, carottage, début fondations"],
                      examples: "Comacchio GEO 305, MC 6, Soilmec SR-15",
                      budget: "150 000 - 350 000 € (neuf)"
                    },
                    {
                      color: "orange",
                      name: "Foreuses lourdes (10-20 tonnes)",
                      target: "Entreprises spécialisées en fondations spéciales",
                      specs: ["Poids : 10 000 - 20 000 kg", "Puissance : 130-200 CV", "Micropieux, ancrages, jet grouting, pieux forés"],
                      examples: "Comacchio MC 15, MC 22, Casagrande C8",
                      budget: "350 000 - 600 000 € (neuf)"
                    },
                    {
                      color: "red",
                      name: "Foreuses très lourdes (+20 tonnes)",
                      target: "Grands chantiers spécialisés, gros projets BTP",
                      specs: ["Poids : 20 000+ kg", "Puissance : 200+ CV", "Grands pieux forés, parois moulées"],
                      examples: "Comacchio MC 28, Soilmec SR-65, Bauer BG 28",
                      budget: "600 000 € - 1 500 000 € (neuf)"
                    },
                  ].map((family, i) => (
                    <div key={i} className={`bg-white rounded-xl border-l-4 ${
                      family.color === 'green' ? 'border-l-green-500' :
                      family.color === 'yellow' ? 'border-l-yellow-500' :
                      family.color === 'orange' ? 'border-l-orange-500' :
                      'border-l-red-500'
                    } shadow-sm p-6 mb-6`}>
                      <h3 className="text-xl font-bold text-[#0A2540] mb-2">{family.name}</h3>
                      <p className="text-[#635BFF] font-medium mb-4">Pour qui ? {family.target}</p>
                      <ul className="space-y-1 mb-4">
                        {family.specs.map((spec, j) => (
                          <li key={j} className="text-gray-600 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                            {spec}
                          </li>
                        ))}
                      </ul>
                      <p className="text-sm text-gray-500 mb-2"><strong>Exemples :</strong> {family.examples}</p>
                      <p className="text-sm font-semibold text-[#0A2540]">Budget indicatif : {family.budget}</p>
                    </div>
                  ))}
                </section>

                {/* Section 3 - Criteria */}
                <section id="section-3" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">3</span>
                    Les 10 critères essentiels pour choisir
                  </h2>

                  <p className="text-gray-700 mb-8">
                    Voici la <strong>checklist complète</strong> à analyser avant tout achat ou location.
                  </p>

                  {/* Criteria list */}
                  {[
                    {
                      num: 1,
                      title: "Le type de travaux à réaliser",
                      stars: 3,
                      content: "C'est LE point de départ. Sondages uniquement → famille 1 ou 2. Micropieux et ancrages → famille 2 ou 3. Pieux forés → famille 3 ou 4.",
                      warning: "Erreur classique : Acheter trop puissant \"au cas où\". Une foreuse de 15T qui fait des sondages de routine = surcoût d'exploitation énorme."
                    },
                    {
                      num: 2,
                      title: "Le couple de rotation",
                      content: "Le couple (mesuré en daNm) détermine la puissance de votre forage. Sondages : 200-500 daNm. Micropieux : 800-3000 daNm. Pieux forés : 3000+ daNm.",
                      tip: "Trop de couple = inutile et cher. Pas assez = travaux impossibles."
                    },
                    {
                      num: 3,
                      title: "La force de poussée",
                      content: "La force de poussée (en daN) détermine la profondeur et la difficulté de sol que vous pouvez traverser. 3000-5000 daN pour sols meubles, 10000+ daN pour roche."
                    },
                    {
                      num: 4,
                      title: "Le poids et le transport",
                      content: "Vérifiez le poids transport, les dimensions, le type de camion nécessaire. Sous 3,5T = transport facile. Au-dessus de 10T = convoi exceptionnel (cher, autorisations).",
                      tip: "Intégrez le coût transport dans votre ROI. C'est souvent oublié !"
                    },
                    {
                      num: 5,
                      title: "L'accessibilité du chantier",
                      content: "Chantier urbain → foreuse compacte (< 1,5m). Intérieur bâtiment → mât télescopique. Pleine campagne → toute taille possible."
                    },
                    {
                      num: 6,
                      title: "Le moteur et la consommation",
                      content: "Moteur Deutz, Kubota ou Caterpillar. Norme Stage V obligatoire en Europe. Consommation : 8-15 L/h selon taille.",
                      warning: "Une foreuse aux normes anciennes (Stage III) sera interdite sur certains chantiers européens."
                    },
                    {
                      num: 7,
                      title: "Les options et accessoires",
                      content: "Une foreuse \"nue\" coûte X. Avec ses options, elle coûte 1,5X à 2X. Pensez aux têtes de rotation, marteaux, pompes, treuils, instrumentation, cage de sécurité."
                    },
                    {
                      num: 8,
                      title: "Le SAV et les pièces détachées",
                      content: "Le plus négligé mais le plus critique ! Vérifiez : distributeur officiel, délai pièces (24h, 48h ?), coût horaire SAV, documentation en français, formation incluse.",
                      tip: "Une foreuse moins chère mais sans SAV en France = catastrophe à l'usage."
                    },
                    {
                      num: 9,
                      title: "La sécurité",
                      content: "Norme EN 16228 OBLIGATOIRE depuis 2014. Cage de sécurité anti-projection, mode ROM, arrêts d'urgence, marquage CE.",
                      warning: "Une foreuse d'occasion ancienne peut ne pas être conforme. Risque d'interdiction sur chantier !"
                    },
                    {
                      num: 10,
                      title: "Le ROI (retour sur investissement)",
                      content: "Comptez : amortissement (5-7 ans), carburant, maintenance (5-10K€/an), SAV, assurance, stockage, transport.",
                      tip: "Une foreuse doit tourner minimum 800 heures/an pour être rentable à l'achat. Sinon, préférez la location."
                    },
                  ].map((criterion) => (
                    <div key={criterion.num} className="bg-white rounded-xl border p-6 mb-4">
                      <h3 className="text-lg font-bold text-[#0A2540] mb-3 flex items-center gap-2">
                        <span className="bg-[#635BFF]/10 text-[#635BFF] w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">
                          {criterion.num}
                        </span>
                        {criterion.title}
                        {criterion.stars && (
                          <span className="text-yellow-500 text-sm">{'⭐'.repeat(criterion.stars)}</span>
                        )}
                      </h3>
                      <p className="text-gray-700 mb-3">{criterion.content}</p>
                      {criterion.tip && (
                        <div className="bg-[#00D4FF]/10 border border-[#00D4FF]/30 rounded-lg p-3 flex items-start gap-2">
                          <Lightbulb className="w-5 h-5 text-[#00D4FF] flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-[#0A2540]"><strong>Conseil pro :</strong> {criterion.tip}</p>
                        </div>
                      )}
                      {criterion.warning && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2 mt-3">
                          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-red-800"><strong>Attention :</strong> {criterion.warning}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </section>

                {/* Section 4 - Buy or Rent */}
                <section id="section-4" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">4</span>
                    Acheter ou louer : quelle option ?
                  </h2>

                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    {/* Buy */}
                    <div className="bg-white rounded-xl border p-6">
                      <h3 className="text-xl font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                        <Euro className="w-6 h-6 text-[#635BFF]" />
                        ACHETER
                      </h3>
                      <h4 className="font-semibold text-green-600 mb-2">Avantages :</h4>
                      <ul className="space-y-1 mb-4">
                        {["Amortissement comptable favorable", "Disponibilité immédiate", "Personnalisation possible", "Pas de coût récurrent"].map((item, i) => (
                          <li key={i} className="text-sm text-gray-600 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            {item}
                          </li>
                        ))}
                      </ul>
                      <h4 className="font-semibold text-red-600 mb-2">Inconvénients :</h4>
                      <ul className="space-y-1 mb-4">
                        {["Investissement lourd (100K€-1M€)", "Risque de sous-utilisation", "Maintenance à charge", "Obsolescence technologique"].map((item, i) => (
                          <li key={i} className="text-sm text-gray-600 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-500" />
                            {item}
                          </li>
                        ))}
                      </ul>
                      <p className="text-sm bg-gray-50 p-3 rounded-lg">
                        <strong>Recommandé si :</strong> activité régulière (&gt; 800h/an), trésorerie solide, stratégie long terme.
                      </p>
                    </div>

                    {/* Rent */}
                    <div className="bg-white rounded-xl border p-6">
                      <h3 className="text-xl font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                        <Truck className="w-6 h-6 text-[#635BFF]" />
                        LOUER
                      </h3>
                      <h4 className="font-semibold text-green-600 mb-2">Avantages :</h4>
                      <ul className="space-y-1 mb-4">
                        {["Pas d'investissement initial", "Charges déductibles 100%", "Flexibilité totale", "Maintenance incluse"].map((item, i) => (
                          <li key={i} className="text-sm text-gray-600 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            {item}
                          </li>
                        ))}
                      </ul>
                      <h4 className="font-semibold text-red-600 mb-2">Inconvénients :</h4>
                      <ul className="space-y-1 mb-4">
                        {["Coût élevé si usage intensif", "Disponibilité non garantie", "Pas de valeur résiduelle", "Engagement minimum (4 mois)"].map((item, i) => (
                          <li key={i} className="text-sm text-gray-600 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-500" />
                            {item}
                          </li>
                        ))}
                      </ul>
                      <p className="text-sm bg-gray-50 p-3 rounded-lg">
                        <strong>Recommandé si :</strong> activité saisonnière, premier projet, trésorerie limitée, test avant achat.
                      </p>
                    </div>
                  </div>

                  <div className="bg-[#635BFF]/5 border border-[#635BFF]/20 rounded-xl p-6">
                    <h3 className="text-lg font-bold text-[#0A2540] mb-2">💡 Option 3 : LOCATION-VENTE (le compromis intelligent)</h3>
                    <p className="text-gray-700 mb-3">
                      Certains distributeurs proposent des formules "location avec option d'achat" : vous louez 12-24 mois, 
                      testez en réel, puis achetez avec déduction des loyers déjà payés. Si pas satisfait, vous rendez sans pénalité.
                    </p>
                    <p className="text-[#635BFF] font-semibold">
                      Ma recommandation : pour un premier achat de foreuse, c'est souvent la meilleure option.
                    </p>
                  </div>
                </section>

                {/* Section 5 - New vs Used */}
                <section id="section-5" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">5</span>
                    Neuf ou occasion : les pièges à éviter
                  </h2>

                  <div className="bg-white rounded-xl border p-6 mb-6">
                    <h3 className="text-xl font-bold text-[#0A2540] mb-4">✅ Les 10 vérifications avant d'acheter une occasion</h3>
                    <div className="grid md:grid-cols-2 gap-2">
                      {[
                        "Heures de fonctionnement (compteur)",
                        "Carnet d'entretien complet",
                        "Date dernière révision majeure",
                        "État du moteur (fuites, fumée)",
                        "État du mât (jeux, soudures)",
                        "État des chenilles (% usure)",
                        "Conformité norme EN 16228",
                        "Marquage CE valide",
                        "Test de forage réel",
                        "Devis de remise en état",
                      ].map((check, i) => (
                        <label key={i} className="flex items-center gap-2 text-gray-700">
                          <input type="checkbox" className="rounded border-gray-300 text-[#635BFF]" />
                          {check}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="bg-[#00D4FF]/10 border border-[#00D4FF]/30 rounded-xl p-6">
                    <Lightbulb className="w-8 h-8 text-[#00D4FF] mb-3" />
                    <p className="text-[#0A2540]">
                      <strong>Conseil pro :</strong> dépenser 500-1000 € pour faire expertiser une foreuse d'occasion 
                      par un professionnel indépendant. C'est l'investissement le plus rentable de votre achat.
                    </p>
                  </div>
                </section>

                {/* Section 6 - Brands */}
                <section id="section-6" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">6</span>
                    Les marques de référence
                  </h2>

                  <div className="space-y-4 mb-8">
                    {[
                      { flag: "🇮🇹", name: "Comacchio", sub: "Le polyvalent", desc: "Gamme très large (1T à 30T+), prix compétitif, fiabilité", rec: "Polyvalence à prix maîtrisé" },
                      { flag: "🇮🇹", name: "Soilmec", sub: "Le premium fondations", desc: "Qualité construction, innovation, gros pieux", rec: "Gros chantiers premium" },
                      { flag: "🇮🇹", name: "Casagrande", sub: "Le robuste", desc: "Solidité, longévité, technologie éprouvée", rec: "Fondations spéciales robustes" },
                      { flag: "🇩🇪", name: "Bauer", sub: "Le haut de gamme", desc: "Excellence allemande, fiabilité maximale", rec: "Gros chantiers premium" },
                      { flag: "🇮🇹", name: "MAIT", sub: "Le challenger", desc: "Innovation technologique, design moderne", rec: "Innovation technologique" },
                      { flag: "🇮🇹", name: "Beretta", sub: "Le spécialiste sondages", desc: "Compacité, sondages profonds", rec: "Sondages géotechniques purs" },
                    ].map((brand, i) => (
                      <div key={i} className="bg-white rounded-xl border p-4 flex items-start gap-4">
                        <span className="text-3xl">{brand.flag}</span>
                        <div className="flex-1">
                          <h3 className="font-bold text-[#0A2540]">{brand.name} <span className="font-normal text-gray-500">— {brand.sub}</span></h3>
                          <p className="text-sm text-gray-600 mb-2">{brand.desc}</p>
                          <span className="inline-block bg-[#635BFF]/10 text-[#635BFF] text-xs font-medium px-2 py-1 rounded">
                            Recommandé pour : {brand.rec}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 7 - Costs */}
                <section id="section-7" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">7</span>
                    Combien ça coûte vraiment ?
                  </h2>

                  <h3 className="text-xl font-bold text-[#0A2540] mb-4">💰 Prix d'achat indicatifs (neuves)</h3>
                  <div className="bg-white rounded-xl border overflow-hidden mb-8">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Famille</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Prix neuf</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Exemple</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        <tr><td className="px-4 py-3">Compactes (1-5T)</td><td className="px-4 py-3 font-semibold text-[#635BFF]">50 000 - 150 000 €</td><td className="px-4 py-3 text-gray-600">Comacchio GEO 300</td></tr>
                        <tr><td className="px-4 py-3">Moyennes (5-10T)</td><td className="px-4 py-3 font-semibold text-[#635BFF]">150 000 - 350 000 €</td><td className="px-4 py-3 text-gray-600">Comacchio GEO 305</td></tr>
                        <tr><td className="px-4 py-3">Lourdes (10-20T)</td><td className="px-4 py-3 font-semibold text-[#635BFF]">350 000 - 600 000 €</td><td className="px-4 py-3 text-gray-600">Comacchio MC 15</td></tr>
                        <tr><td className="px-4 py-3">Très lourdes (+20T)</td><td className="px-4 py-3 font-semibold text-[#635BFF]">600 000 - 1 500 000 €</td><td className="px-4 py-3 text-gray-600">Bauer BG 28</td></tr>
                      </tbody>
                    </table>
                  </div>

                  <p className="text-gray-600 text-sm mb-8">
                    Ces prix sont indicatifs et varient selon la configuration, les options et le distributeur. 
                    <Link to="/materiel/demande?type=foreuse" className="text-[#635BFF] font-medium hover:underline ml-1">
                      Demandez un devis personnalisé pour votre besoin →
                    </Link>
                  </p>

                  <h3 className="text-xl font-bold text-[#0A2540] mb-4">🛠️ Coûts annexes à NE PAS oublier</h3>
                  <div className="bg-white rounded-xl border overflow-hidden mb-8">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Poste</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Coût annuel</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        <tr><td className="px-4 py-3">Maintenance préventive</td><td className="px-4 py-3">5 000 - 15 000 €</td></tr>
                        <tr><td className="px-4 py-3">Pièces d'usure</td><td className="px-4 py-3">10 000 - 50 000 €</td></tr>
                        <tr><td className="px-4 py-3">Carburant (1 000h/an)</td><td className="px-4 py-3">10 000 - 25 000 €</td></tr>
                        <tr><td className="px-4 py-3">Assurance</td><td className="px-4 py-3">1-2% valeur machine</td></tr>
                        <tr><td className="px-4 py-3">Formation opérateur</td><td className="px-4 py-3">1 500 - 5 000 €</td></tr>
                        <tr><td className="px-4 py-3">Transport (par chantier)</td><td className="px-4 py-3">500 - 5 000 €</td></tr>
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Section 8 - Traps */}
                <section id="section-8" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">8</span>
                    Les 7 pièges classiques à éviter
                  </h2>

                  <div className="space-y-4">
                    {[
                      { title: "Acheter trop puissant \"au cas où\"", desc: "Une MC 15 pour des sondages de routine = surcoût d'exploitation énorme." },
                      { title: "Acheter sans SAV proche", desc: "Pannes immobilisantes, pièces qui arrivent en 3 semaines." },
                      { title: "Foreuse d'occasion sans expertise", desc: "30 000 € de réparations dans les 6 mois." },
                      { title: "Oublier les normes", desc: "Interdiction sur chantier européen, perte de marchés." },
                      { title: "Sous-estimer le transport", desc: "2 000 € de transport par chantier, autorisations préfectorales." },
                      { title: "Pas de formation opérateur", desc: "Mauvais usage = panne accélérée + chantiers ratés." },
                      { title: "Ne pas tester avant d'acheter", desc: "La machine ne correspond pas à votre attente." },
                    ].map((trap, i) => (
                      <div key={i} className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                        <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-bold text-red-800">Piège {i + 1} : {trap.title}</h4>
                          <p className="text-red-700 text-sm">{trap.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 9 - Conclusion */}
                <section id="section-9" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">9</span>
                    Conclusion et plan d'action
                  </h2>

                  <p className="text-gray-700 mb-6">
                    Choisir une foreuse géotechnique est un <strong>investissement majeur</strong> qui demande de la méthode. 
                    Voici votre <strong>plan d'action concret</strong> en 7 étapes :
                  </p>

                  <div className="bg-white rounded-xl border p-6 mb-8">
                    <ol className="space-y-4">
                      {[
                        "Définir précisément vos travaux (sondages, micropieux, pieux forés ?)",
                        "Choisir votre famille de foreuse (compacte / moyenne / lourde)",
                        "Définir vos contraintes (budget, accessibilité, délai)",
                        "Acheter ou louer ? (> 800h/an = achat, sinon location)",
                        "Sélectionner 3-5 modèles candidats et demander des devis",
                        "Tester et négocier (démonstration, options, garantie)",
                        "Finaliser et planifier (conformité, formation, maintenance)",
                      ].map((step, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span className="bg-[#635BFF] text-white w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                            {i + 1}
                          </span>
                          <span className="text-gray-700">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </section>

                {/* CTA Section */}
                <section className="bg-gradient-to-br from-[#635BFF] to-[#8B5CF6] rounded-2xl p-8 text-white mb-16">
                  <h2 className="text-2xl font-bold mb-4">🚀 Comment GEOTS Consulting peut vous aider</h2>
                  <p className="mb-6 text-white/90">
                    Sur geots.consulting, retrouvez le catalogue de foreuses géotechniques, le comparateur IA, 
                    les fournisseurs vérifiés et la demande de devis multiples.
                  </p>
                  <div className="bg-white/10 rounded-xl p-4 mb-6">
                    <p className="font-semibold">🎁 Offre de lancement</p>
                    <p className="text-white/90">
                      Les 100 premiers fournisseurs bénéficient de 6 mois Premium offerts. Sans engagement, sans carte bancaire.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-4">
                    <Link to="/shop" className="bg-white text-[#635BFF] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2">
                      Voir les foreuses
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                    <Link to="/register" className="bg-white/20 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/30 transition-colors">
                      S'inscrire gratuitement
                    </Link>
                  </div>
                </section>

                {/* FAQ Section */}
                <section className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <HelpCircle className="w-8 h-8 text-[#635BFF]" />
                    Questions fréquentes
                  </h2>

                  <div className="space-y-3">
                    {faqs.map((faq, i) => (
                      <div key={i} className="bg-white rounded-xl border overflow-hidden">
                        <button
                          onClick={() => setOpenFaq(openFaq === i ? null : i)}
                          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-semibold text-[#0A2540]">{faq.question}</span>
                          {openFaq === i ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                        {openFaq === i && (
                          <div className="px-6 pb-4">
                            <p className="text-gray-700">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>

                {/* Related Guides */}
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#0A2540] mb-6">📚 Pour aller plus loin</h2>
                  <div className="grid md:grid-cols-3 gap-4">
                    <Link 
                      to="/guides/comment-choisir-etude-geotechnique"
                      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-[#0A2540] mb-2">Comment choisir son étude géotechnique ?</p>
                      <span className="text-sm text-[#635BFF] flex items-center gap-1">
                        Lire le guide
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                    <Link 
                      to="/guides/comment-choisir-fondations-speciales"
                      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-[#0A2540] mb-2">Comment choisir ses fondations spéciales ?</p>
                      <span className="text-sm text-[#635BFF] flex items-center gap-1">
                        Lire le guide
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                    {[
                      "Acheter ou louer : 7 critères pour décider",
                    ].map((title, i) => (
                      <div key={i} className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow cursor-pointer">
                        <p className="font-medium text-[#0A2540] mb-2">{title}</p>
                        <span className="text-sm text-[#635BFF] flex items-center gap-1">
                          Bientôt disponible
                          <ExternalLink className="w-4 h-4" />
                        </span>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Footer note */}
                <div className="text-center text-gray-500 text-sm border-t pt-8">
                  <p>
                    Ce guide a été rédigé par l'équipe de GEOTS Consulting, marketplace 100% spécialisée 
                    géotechnique et fondations spéciales.
                  </p>
                  <p className="mt-2">📅 Dernière mise à jour : 23 juillet 2026</p>
                </div>
              </article>
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default HowToChooseDrilling;
